import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import from generated schemas
const {
  CreateTransactionDto,
  TransactionFirestoreResponse,
} = schemas;

// Re-export schemas for external use
export const TransactionResponseSchema = TransactionFirestoreResponse;
export const CreateTransactionSchema = CreateTransactionDto;

// ====== Comparison Schema ======
// Note: This is a custom schema not in generated (comparison endpoints are utility)
export const TransactionComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: TransactionResponseSchema.nullable(),
}).passthrough();

// ====== Type Exports ======

export type TransactionResponse = z.infer<typeof TransactionResponseSchema>;
export type CreateTransaction = z.infer<typeof CreateTransactionSchema>;
export type TransactionComparisonResult = z.infer<typeof TransactionComparisonResultSchema>;

/**
 * Create transactions API factory
 */
export function createTransactionsApi(client: TypedApiClient) {
  return {
    /**
     * Get transactions with optional filters
     */
    getTransactions: (params?: {
      storeId?: string;
      type?: 'income' | 'expense' | 'transfer';
      walletId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      /**
       * ATL-986: filter by the counterparty in `externalDestination.id` (a supplier
       * on supply payments). Accepts the Firestore id or the Supabase UUID.
       */
      externalDestinationId?: string;
    }): Promise<TransactionResponse[]> => {
      return client.get('/transactions', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(TransactionResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/transactions` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getTransactionsWindow: (
      params: {
      storeId?: string;
      type?: 'income' | 'expense' | 'transfer';
      walletId?: string;
      startDate?: string;
      endDate?: string;
      linkedDocId?: string;
      linkedDocType?: string;
      externalDestinationId?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof TransactionResponseSchema>>> => {
      return client.get('/transactions/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(TransactionResponseSchema),
      });
    },

    /**
     * Get a single transaction by ID
     */
    getTransactionById: (id: string): Promise<TransactionResponse> => {
      return client.get(apiPath`/transactions/${id}`, {
        responseSchema: TransactionResponseSchema,
      });
    },

    /**
     * Create a new transaction
     */
    createTransaction: (data: CreateTransaction): Promise<TransactionResponse> => {
      return client.post('/transactions', {
        data,
        requestSchema: CreateTransactionSchema,
        responseSchema: TransactionResponseSchema,
      });
    },

    /**
     * Delete a transaction
     */
    deleteTransaction: (id: string) => {
      return client.delete(apiPath`/transactions/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare transaction with Firestore data
     */
    compareWithFirestore: (firestoreId: string): Promise<TransactionComparisonResult> => {
      return client.get(apiPath`/transactions/compare/${firestoreId}`, {
        responseSchema: TransactionComparisonResultSchema,
      });
    },
  };
}
